import React from "react";
import { cn } from "@/lib/utils";
import { Upsell, isVariableCountUpsell } from "@/data/upsells";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLocalizedUpsellCopy } from "@/utils/bookLocalizedCopy";
import { Check, Minus, Plus } from "lucide-react";

const MAX_COUNT = 4;

interface UpsellCardProps {
  upsell: Upsell;
  /** 0 = not selected; 1–4 for variable-count; 1 = selected for single-product */
  count: number;
  onCountChange: (count: number) => void;
  /** Pre-formatted unit sell price (display currency from Mews product map or CHF fallback). */
  sellFormatted: string;
  /** Pre-formatted unit list price for strikethrough when discount applies; omit when no strike. */
  listFormatted?: string | null;
  /** Optional micro-copy for PerPerson / PerPersonPerTimeUnit products (per-room enhance step). */
  pricedForRoomGuestsHint?: string | null;
}

/** Mobile stack: image is full-width above text; bias crop upward for these assets. */
const UPSELL_IMAGE_FOCUS_TOP_MOBILE: ReadonlySet<string> = new Set([
  "romantic-setup",
  "massage-express-25",
]);

export const UpsellCard = ({
  upsell,
  count,
  onCountChange,
  sellFormatted,
  listFormatted,
  pricedForRoomGuestsHint,
}: UpsellCardProps) => {
  const { translations } = useLanguage();
  const book = translations.book;
  const { name, description } = getLocalizedUpsellCopy(
    upsell.id,
    { name: upsell.name, description: upsell.description },
    book,
  );
  const categoryLabel = book.upsellCategoryLabels[upsell.category] ?? upsell.category;
  const uc = book.upsellCard;

  const isVariable = isVariableCountUpsell(upsell);
  const isSelected = count > 0;
  const focusTopMobile = UPSELL_IMAGE_FOCUS_TOP_MOBILE.has(upsell.id);

  const handleCardClick = () => {
    if (isVariable) {
      if (count === 0) onCountChange(1);
      return;
    }
    onCountChange(isSelected ? 0 : 1);
  };

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (count < MAX_COUNT) onCountChange(count + 1);
  };

  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (count > 0) onCountChange(count - 1);
  };

  return (
    <div
      className={`
        relative overflow-hidden rounded-xl border transition-all duration-300 group
        ${isSelected
          ? "border-alpine-gold bg-white shadow-md ring-1 ring-alpine-gold/25"
          : "border-stone-200 bg-white hover:border-alpine-gold/50 hover:shadow-sm"
        }
        ${!isVariable || count === 0 ? "cursor-pointer" : ""}
      `}
      onClick={handleCardClick}
      role={isVariable && count > 0 ? undefined : "button"}
    >
      <div className="flex flex-col sm:flex-row h-full">
        {/* Image Section */}
        <div className="sm:w-32 h-32 sm:h-auto relative shrink-0">
          <img
            src={upsell.image}
            alt={name}
            className={cn(
              "w-full h-full object-cover transition-transform duration-500 group-hover:scale-105",
              focusTopMobile && "object-[center_22%] sm:object-center",
            )}
          />
          {isSelected && !isVariable && (
            <div className="absolute inset-0 bg-alpine-gold/20 flex items-center justify-center backdrop-blur-[1px]">
              <div className="bg-white rounded-full p-1 shadow-sm">
                <Check className="w-5 h-5 text-alpine-gold" />
              </div>
            </div>
          )}
        </div>

        {/* Content Section - Lato for body text to match room cards */}
        <div className="p-4 flex flex-col justify-between flex-grow font-lato">
          <div>
            <div className="mb-1 flex items-start justify-between gap-2 sm:gap-3">
              <span className="min-w-0 flex-1 pt-0.5 text-xs font-bold uppercase tracking-wider text-neutral-600">
                {categoryLabel}
              </span>
              <div className="flex min-w-0 max-w-[55%] flex-col items-end gap-0.5 text-right">
                {listFormatted ? (
                  <span className="max-w-full break-words text-sm font-medium leading-tight text-rose-950/50 line-through decoration-rose-400/45">
                    {listFormatted}
                  </span>
                ) : null}
                <span className="max-w-full break-words font-bold leading-tight text-lg text-pine-green">
                  {sellFormatted}
                  {isVariable && count > 0 && (
                    <span className="text-sm font-normal text-neutral-600">
                      {" "}
                      × {count}
                    </span>
                  )}
                </span>
              </div>
            </div>
            <h3 className="font-playfair text-lg text-neutral-900 mb-2 leading-tight">
              {name}
            </h3>
            <p className="line-clamp-4 text-sm leading-snug text-stone-600">
              {description}
            </p>
            {pricedForRoomGuestsHint ? (
              <p className="mt-1.5 text-xs text-stone-500">{pricedForRoomGuestsHint}</p>
            ) : null}
          </div>

          <div className="mt-4 flex justify-end items-center gap-3">
            {isVariable && count > 0 ? (
              <div className="flex items-center gap-1 rounded-full border border-stone-200 bg-white overflow-hidden">
                <button
                  type="button"
                  aria-label={uc.decreaseAria}
                  onClick={handleDecrement}
                  disabled={count <= 0}
                  className="p-2 text-stone-600 hover:bg-stone-100 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="min-w-[2rem] text-center text-sm font-semibold text-neutral-900 tabular-nums">
                  {count}
                </span>
                <button
                  type="button"
                  aria-label={uc.increaseAria}
                  onClick={handleIncrement}
                  disabled={count >= MAX_COUNT}
                  className="p-2 text-stone-600 hover:bg-stone-100 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onCountChange(isSelected ? 0 : 1);
                }}
                className={`
                  flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-colors
                  ${isSelected
                    ? "bg-alpine-gold text-white shadow-sm"
                    : "bg-stone-100 text-stone-600 group-hover:bg-alpine-gold/10 group-hover:text-alpine-gold"
                  }
                `}
              >
                {isSelected ? (
                  <>
                    <Check className="w-4 h-4" />
                    <span>{uc.added}</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    <span>{uc.add}</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
